// ============================================================
// THE TRACES (Squad Mode stage 8, board 10i): the life signs drawn as
// paths for the Solo lane - a wide ECG and a narrow EEG over it - flowing
// DOWN the lane. One LOOP is 240 px of screen at 60 px/s (4 s): a path
// spans y 0..LOOP, its two ends on the baseline, so two copies scroll
// seamlessly under one CSS translate (never a per-frame rebuild).
//
// The ECG is drawn in straight segments, the QRS never smoothed. Beats per
// loop are whole (a rate is read to the nearest beat a loop allows), so
// the loop closes. The EEG is a sum of waves whose cycles fit the loop
// whole. Amplitude scales with the lane's width; the beat spacing stays in
// screen px. Noise is seeded (`hash01`), never Math.random (gotcha 5).
//
// Pure. Zero React dependency.
// ============================================================
import { hash01 } from './hash';
import type { LifeSigns, Rhythm, Wave } from './lifeSigns';

/** One loop of the traces on screen, px (60 px/s for 4 s). */
export const LOOP = 240;

type Pt = [number, number];
const path = (pts: readonly Pt[]) => pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`).join('');

/** A beat's complex as [y along the beat 0..1, deflection -1..1]: P, PR, QRS (straight), ST at `st`, T. */
function complex(kind: 'normal' | 'pvc' | 'dropped' | 'noP' | 'longPR', st: number): Pt[] {
  const p: Pt[] = kind === 'noP' ? [[0.1, 0]] : [[0.06, 0], [0.1, 0.12], [0.14, 0]];
  const pr = kind === 'longPR' ? 0.26 : 0.18;
  if (kind === 'dropped') return [...p, [0.5, 0]];
  const qrs: Pt[] =
    kind === 'pvc'
      ? [[pr, 0], [pr + 0.04, -0.3], [pr + 0.1, 0.9], [pr + 0.18, -0.6], [pr + 0.24, -st]]
      : [[pr, 0], [pr + 0.02, -0.15], [pr + 0.04, 1], [pr + 0.06, -0.35], [pr + 0.08, -st]];
  const t: Pt[] = [[pr + 0.3, -st], [pr + 0.38, 0.22], [pr + 0.46, 0]];
  return [...p, ...qrs, ...t];
} // prettier-ignore

/** Which complex beat `i` of a loop's `n` draws, by rhythm. */
const BEAT: Partial<Record<Rhythm, (i: number) => Parameters<typeof complex>[0]>> = {
  avBlock2: (i) => (i % 3 === 2 ? 'dropped' : 'normal'),
  ectopics: (i) => (i % 4 === 3 ? 'pvc' : 'normal'),
  svt: () => 'noP',
  bradyBlock1: () => 'longPR',
  shock: (i) => (i % 5 === 4 ? 'dropped' : 'normal'),
};

/** Beats in one loop: the rate read to the nearest whole beat the loop allows (4 s a loop). */
const beatsPerLoop = (bpm: number) => Math.max(1, Math.round((bpm * (LOOP / 60)) / 60));

function ecgPoints(heart: LifeSigns['heart'], amp: number, seed: number): Pt[] {
  if (heart.rhythm === 'asystole') return [[0, 0], [0, LOOP]];
  if (heart.rhythm === 'vf') return noise(amp * 0.6, 96, seed);
  const n = beatsPerLoop(heart.bpm);
  const span = LOOP / n;
  const pts: Pt[] = [[0, 0]];
  for (let i = 0; i < n; i++) {
    const kind = BEAT[heart.rhythm]?.(i) ?? 'normal';
    // A premature atrial beat: every third comes a little early - its P close on the last T. The loop still closes:
    // the beats are whole and each keeps its own span.
    const shift = heart.rhythm === 'pac' && i % 3 === 1 ? -0.05 : 0;
    for (const [t, d] of complex(kind, heart.st)) pts.push([d * amp * heart.amp, (i + Math.max(0, t + shift)) * span]);
  }
  pts.push([0, LOOP]);
  return pts;
} // prettier-ignore

/** Seeded noise along the loop, its ends on the baseline. */
function noise(amp: number, steps: number, seed: number): Pt[] {
  return Array.from({ length: steps + 1 }, (_, i): Pt => {
    const edge = i === 0 || i === steps;
    return [edge ? 0 : (hash01(seed, i) * 2 - 1) * amp, (i / steps) * LOOP];
  });
}

/** The heart's artefacts laid over its trace: a tremor's fine shake, the mains hum and a stray pacing spike. */
function withArtefacts(pts: Pt[], heart: LifeSigns['heart'], amp: number, seed: number): Pt[] {
  if (!heart.artefacts.length) return pts;
  const fine = (y: number, i: number) =>
    (heart.artefacts.includes('tremor') ? (hash01(seed + 7, i) - 0.5) * amp * 0.12 : 0) +
    // 30 cycles a loop (an 8 px period): sampled every 2 px it reads as a hum, not the aliased nothing 60 gave.
    (heart.artefacts.includes('mains') ? Math.sin((y / LOOP) * Math.PI * 2 * 30) * amp * 0.06 : 0);
  const dense = densify(pts, 2);
  return dense.map(([x, y], i): Pt => (i === 0 || i === dense.length - 1 ? [x, y] : [x + fine(y, i), y]));
}

/** Points every `step` px along the loop, so an artefact has somewhere to show. */
function densify(pts: readonly Pt[], step: number): Pt[] {
  const out: Pt[] = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const [[x0, y0], [x1, y1]] = [pts[i], pts[i + 1]];
    const n = Math.max(1, Math.ceil((y1 - y0) / step));
    for (let k = 0; k < n; k++) out.push([x0 + ((x1 - x0) * k) / n, y0 + ((y1 - y0) * k) / n]);
  }
  out.push(pts[pts.length - 1]);
  return out;
}

/** The ECG for one loop, as a path: x across the lane (0 = its baseline), y down it. `amp` is half the swing, px. */
export function ecgPath(heart: LifeSigns['heart'], amp: number, seed = 1): string {
  return path(withArtefacts(ecgPoints(heart, amp, seed), heart, amp, seed));
}

/** Each wave as [cycles per loop, relative size]; the sizes sum to about one. Cycles are whole: the loop closes. */
const WAVES: Record<Wave, readonly (readonly [number, number])[]> = {
  flat: [],
  fading: [[9, 0.25], [23, 0.1]],
  delta: [[3, 0.8], [5, 0.2]],
  burstSuppression: [[31, 0.7]],
  chaos: [[7, 0.4], [13, 0.35], [29, 0.3], [41, 0.2]],
  theta: [[11, 0.6], [17, 0.3]],
  gamma: [[67, 0.4], [89, 0.3], [113, 0.2]],
  flutter: [[47, 0.5], [53, 0.4]],
  fearSpikes: [[19, 0.5], [37, 0.2]],
  eyesClosedAlpha: [[19, 0.9]],
  alpha: [[19, 0.6], [29, 0.2]],
  beta: [[29, 0.35], [43, 0.25], [61, 0.15]],
}; // prettier-ignore

/** How each wave is shaped over the loop, beyond its sum of sines: bursts, alternating hemispheres, a fade. */
const ENVELOPE: Partial<Record<Wave, (u: number) => number>> = {
  burstSuppression: (u) => (u % 0.25 < 0.08 ? 1 : 0.05),
  delta: (u) => (u < 0.5 ? 1 : -1),
  fading: (u) => 0.4 + 0.6 * Math.abs(Math.sin(u * Math.PI * 3)),
  flutter: (u) => 0.3 + 0.7 * Math.abs(Math.sin(u * Math.PI * 4)),
};

/** The EEG at `u` (0..1 along the loop), before its size. */
function eegAt(wave: Wave, u: number, phase: number): number {
  const sum = WAVES[wave].reduce(
    (s, [cycles, size], k) => s + size * Math.sin(2 * Math.PI * (cycles * u + phase * (k + 1))),
    0,
  );
  return sum * (ENVELOPE[wave]?.(u) ?? 1);
}

/** A power's spike trains: every 48 samples a train of `trains` spikes, alternating, standing out of the wave. */
const trainAt = (trains: number, i: number) => {
  const pos = i % 48;
  return pos < trains * 2 ? (pos % 2 ? -1.1 : 1.1) : 0;
};

/** The mind's artefacts: pain's spikes, Haywire's digital steps, a power's trains. */
function mindArtefact(mind: LifeSigns['mind'], x: number, i: number, steps: number): number {
  // The loop's two ends stay as the waves leave them (equal: the cycles are whole), so the seam never jumps.
  if (i === 0 || i === steps) return x;
  const v = mind.artefacts.includes('digitalSteps') ? Math.round(x * 4) / 4 : x;
  return v + SPIKES.reduce((sum, spike) => sum + spike(mind, i), 0);
}

/** What stands out of the wave at sample `i`: pain's spikes, a failed Fear test's, a power's trains. */
const SPIKES: readonly ((mind: LifeSigns['mind'], i: number) => number)[] = [
  (mind, i) => (mind.artefacts.includes('painSpikes') && i % 23 === 0 ? 0.8 : 0),
  (mind, i) => (mind.wave === 'fearSpikes' && i % 31 === 0 ? 1 : 0),
  (mind, i) => (mind.trains > 0 ? trainAt(mind.trains, i) : 0),
];

/**
 * The EEG for one loop, as a path: `spread` px of swing at full; a ghost (`phase` apart) for the hallucination's
 * drifting second line. Its two ends meet, so the copies join seamlessly.
 */
export function eegPath(mind: LifeSigns['mind'], spread: number, phase = 0): string {
  const steps = 160;
  const size = spread * mind.spread;
  // No end is forced to the baseline: whole cycles meet themselves, a ghost's phase included (the stage 8 review).
  const pts = Array.from({ length: steps + 1 }, (_, i): Pt => {
    const u = i / steps;
    return [mindArtefact(mind, eegAt(mind.wave, u, phase), i, steps) * size, u * LOOP];
  });
  return path(pts);
}
